import React, { useEffect, useState } from 'react';
import {
  getConnectStatus,
  startOnboarding,
  getDashboardLink,
  disconnectStripe,
  ConnectStatus,
} from '../services/paymentService';

const PaymentSettings: React.FC = () => {
  const [status, setStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const stripeConfigured = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const st = await getConnectStatus();
      setStatus(st);
    } catch (e) {
      setError('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  }

  async function handleOnboard() {
    setOnboarding(true);
    setError(null);
    try {
      const url = await startOnboarding();
      const popup = window.open(url, 'stripe_connect', 'width=800,height=700,left=200,top=100');

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'stripe_connect') return;
        window.removeEventListener('message', handleMessage);
        clearInterval(pollClosed);
        setOnboarding(false);
        if (event.data.status === 'connected') {
          setSuccess('Stripe connected successfully!');
          load();
        } else {
          setError('Stripe setup link expired. Please try again.');
        }
      };

      window.addEventListener('message', handleMessage);

      const pollClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollClosed);
          window.removeEventListener('message', handleMessage);
          setOnboarding(false);
        }
      }, 1000);
    } catch (e: any) {
      setError(e.message || 'Failed to start Stripe onboarding');
      setOnboarding(false);
    }
  }

  async function handleDashboardLink() {
    setError(null);
    try {
      const url = await getDashboardLink();
      window.open(url, '_blank');
    } catch (e: any) {
      setError(e.message || 'Failed to open Stripe dashboard');
    }
  }

  async function handleDisconnect() {
    setDisconnecting(true);
    setError(null);
    setConfirmDisconnect(false);
    try {
      await disconnectStripe();
      setSuccess('Stripe account disconnected.');
      await load();
    } catch (e: any) {
      setError(e.message || 'Failed to disconnect Stripe');
    } finally {
      setDisconnecting(false);
    }
  }

  if (!stripeConfigured) {
    return (
      <div className="py-4 text-center">
        <div className="w-10 h-10 rounded-full bg-dark-hover flex items-center justify-center mx-auto mb-3">
          <svg className="w-5 h-5 text-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
          </svg>
        </div>
        <p className="text-sm font-medium text-gray-200">Payments not configured</p>
        <p className="text-xs text-dark-muted mt-1">Set <code className="text-violet-400">REACT_APP_STRIPE_PUBLISHABLE_KEY</code> to enable payments.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-6 flex justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-400" />
      </div>
    );
  }

  const connected = status?.onboarding_complete && status?.charges_enabled;

  return (
    <div className="space-y-5 py-2">
      {error && (
        <div className="text-xs text-red-400 bg-red-900/20 rounded-lg px-3 py-2">{error}</div>
      )}
      {success && (
        <div className="text-xs text-green-400 bg-green-900/20 rounded-lg px-3 py-2">{success}</div>
      )}

      {/* ── Stripe Connect status ── */}
      <div className="bg-dark-hover rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-dark-muted uppercase tracking-wide">Stripe Account</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            connected
              ? 'bg-green-900/40 text-green-400'
              : status?.stripe_account_id
              ? 'bg-yellow-900/40 text-yellow-400'
              : 'bg-gray-700 text-dark-muted'
          }`}>
            {connected ? 'Active' : status?.stripe_account_id ? 'Incomplete' : 'Not connected'}
          </span>
        </div>

        {connected ? (
          <div className="space-y-2">
            <p className="text-xs text-dark-muted">
              Payouts {status?.payouts_enabled ? 'enabled' : 'pending'} · Charges enabled
            </p>
            <button
              onClick={handleDashboardLink}
              className="w-full text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-3 py-2 transition-colors"
            >
              Open Stripe Dashboard
            </button>
            {confirmDisconnect ? (
              <div className="rounded-lg border border-red-800/50 bg-red-900/20 px-3 py-3 space-y-2">
                <p className="text-xs text-red-300">Disconnect your Stripe account? Clients won't be able to pay via Stripe until you reconnect.</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleDisconnect}
                    disabled={disconnecting}
                    className="flex-1 text-xs bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg px-3 py-1.5 transition-colors"
                  >
                    {disconnecting ? 'Disconnecting…' : 'Yes, disconnect'}
                  </button>
                  <button
                    onClick={() => setConfirmDisconnect(false)}
                    className="flex-1 text-xs bg-dark-hover hover:bg-dark-border text-gray-300 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmDisconnect(true)}
                className="w-full text-xs text-red-400 hover:text-red-300 py-1 transition-colors"
              >
                Disconnect Stripe
              </button>
            )}
          </div>
        ) : status?.stripe_account_id && !status?.onboarding_complete ? (
          <div className="space-y-2">
            <p className="text-xs text-dark-muted">Complete your Stripe onboarding to accept payments.</p>
            <button
              onClick={handleOnboard}
              disabled={onboarding}
              className="w-full text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg px-3 py-2 transition-colors"
            >
              {onboarding ? 'Opening…' : 'Continue Onboarding'}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-dark-muted">Connect Stripe to collect session payments directly through CoachSight.</p>
            <button
              onClick={handleOnboard}
              disabled={onboarding}
              className="w-full text-sm bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white rounded-lg px-3 py-2 transition-colors"
            >
              {onboarding ? 'Opening…' : 'Connect Stripe'}
            </button>
          </div>
        )}
      </div>

    </div>
  );
};

export default PaymentSettings;
